import React, { useState, useMemo, useEffect } from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Button } from '../components/button';
import { Field, Label } from '../components/fieldset';
import { Input } from '../components/input';
import { Listbox, ListboxOption, ListboxLabel } from '../components/listbox';
import { Table, TableHeader, TableHead, TableRow, TableCell, TableBody } from '../components/table';
import { addDoc, arrayUnion, arrayRemove, collection, doc, updateDoc, deleteDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useComisionConfig, useComisionLedger } from '../contexts/DataContext';
import { partitionTarifas, DEFAULT_PARTICIPACION, TARIFA_AMBITO_CONVENIO } from '../utils/calculations';
import { findRetarifaCandidates, buildRetarifaPlan } from '../utils/retarifa';
import RetarifaComisionesDialog from '../components/RetarifaComisionesDialog';
import TablePager from '../components/TablePager';

const minTarifaDefault = { min: 0.71, max: 0.66, comision: 19 };
const defaultTramos = [
  { min: 1.2, max: 1.14, comision: 35 },
  { min: 1.13, max: 1.08, comision: 33 },
  { min: 1.07, max: 1.02, comision: 31 },
  { min: 1.01, max: 0.96, comision: 29 },
  { min: 0.95, max: 0.9, comision: 27 },
  { min: 0.89, max: 0.84, comision: 25 },
  { min: 0.83, max: 0.78, comision: 23 },
  { min: 0.77, max: 0.72, comision: 21 },
  minTarifaDefault,
];

// Participación de convenios per afianzadora. Every afianzadora seen in the
// org's fianzas gets a row prefilled with its saved split or the 70/30 default
// — no add step. Saving persists all rows.
const PARTICIPACION_POR_PAGINA = 10;

const ParticipacionConvenios = ({ afianzadoras }) => {
  const { config, loading, saveComisionConfig } = useComisionConfig();
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const saved = config?.participacionConvenio || {};
    const emisores = Array.from(new Set([...afianzadoras, ...Object.keys(saved)]))
      .sort((a, b) => a.localeCompare(b, "es"));
    setDraft(Object.fromEntries(
      emisores.map((name) => [name, { ...DEFAULT_PARTICIPACION, ...(saved[name] || {}) }])
    ));
  }, [config?.participacionConvenio, afianzadoras]);

  const setPct = (emisor, campo, raw) => {
    const val = raw === "" ? "" : parseFloat(raw);
    setDraft((prev) => {
      const entry = { ...(prev[emisor] || {}) };
      entry[campo] = val;
      // fiado auto-fills as the complement while it has not been hand-edited.
      if (campo === "beneficiario" && typeof val === "number" && !entry.fiadoManual) {
        entry.fiado = Math.max(0, Math.round((100 - val) * 100) / 100);
      }
      if (campo === "fiado") entry.fiadoManual = true;
      return { ...prev, [emisor]: entry };
    });
  };

  const save = async () => {
    setError(null);
    const clean = {};
    for (const [emisor, entry] of Object.entries(draft)) {
      const beneficiario = Number(entry.beneficiario);
      const fiado = Number(entry.fiado);
      if (
        Number.isNaN(beneficiario) || Number.isNaN(fiado) ||
        beneficiario < 0 || beneficiario > 100 || fiado < 0 || fiado > 100
      ) {
        setError(`Porcentajes inválidos para ${emisor}: deben estar entre 0 y 100.`);
        return;
      }
      if (beneficiario + fiado > 100) {
        setError(`La suma de porcentajes de ${emisor} no puede exceder 100.`);
        return;
      }
      clean[emisor] = { beneficiario, fiado };
    }
    setSaving(true);
    try {
      await saveComisionConfig({ participacionConvenio: clean });
    } catch (e) {
      console.error("Error guardando participación:", e);
      setError("No se pudo guardar la participación.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div className="flex items-center justify-between">
        <Heading level={3} className="text-xl">Participación de convenios</Heading>
        <Button onClick={save} disabled={saving || loading}>
          {saving ? "Guardando..." : "Guardar"}
        </Button>
      </div>

      {error && <Text className="text-red-600 text-sm">{error}</Text>}

      {loading ? (
        <Text>Cargando...</Text>
      ) : Object.keys(draft).length === 0 ? (
        <Text className="text-sm text-azul-surelia/70">Sin afianzadoras registradas.</Text>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <Table>
            <TableHead className="bg-azul-surelia text-white">
              <TableRow>
                <TableHeader className="text-white">Afianzadora</TableHeader>
                <TableHeader className="text-white">Convenio (beneficiario) %</TableHeader>
                <TableHeader className="text-white">Fiado %</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(draft)
                .slice((page - 1) * PARTICIPACION_POR_PAGINA, page * PARTICIPACION_POR_PAGINA)
                .map(([emisor, entry]) => (
                <TableRow key={emisor}>
                  <TableCell className="font-medium">{emisor}</TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={entry.beneficiario}
                      onChange={(e) => setPct(emisor, "beneficiario", e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      step="1"
                      min="0"
                      max="100"
                      value={entry.fiado}
                      onChange={(e) => setPct(emisor, "fiado", e.target.value)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="px-3">
            <TablePager
              total={Object.keys(draft).length}
              page={page}
              onPage={setPage}
              perPage={PARTICIPACION_POR_PAGINA}
              itemLabel="afianzadoras"
              compact
            />
          </div>
        </div>
      )}
    </div>
  );
};

const ConfiguracionComisiones = ({
  user,
  userData,
  tarifas,
  setTarifas,
  tarifasLoading,
  tarifasError,
  setTarifasError,
  contractsList,
}) => {
  // Local state for UI
  const [expandedTarifas, setExpandedTarifas] = useState(new Set());
  const [isCreatingNewTarifa, setIsCreatingNewTarifa] = useState(false);
  const [savingTarifaId, setSavingTarifaId] = useState(null);
  // Section tabs: the org's own tables vs everything convenio-related
  // (participación + default convenio tarifa estimates).
  const [seccion, setSeccion] = useState("propias");
  // After a table save: fianzas with PENDING comisión priced off the replaced
  // pricing, offered for owner-confirmed retarificación.
  const [retarifa, setRetarifa] = useState(null);
  const { movements } = useComisionLedger();

  const proposeRetarifa = (tarifaCambiada, tarifasActuales, esNueva) => {
    const candidates = findRetarifaCandidates(movements, tarifaCambiada, {
      esNueva,
      tarifas: tarifasActuales,
    });
    const plan = buildRetarifaPlan(candidates, contractsList, tarifasActuales, tarifaCambiada);
    if (plan.length > 0) setRetarifa({ plan, tarifaId: tarifaCambiada.id });
  };
  const [newTarifa, setNewTarifa] = useState({
    emisor: "",
    subRamo: "",
    tipo: "",
    tramos: defaultTramos.map((t) => ({ ...t })),
  });

  // Propias price the org's own fianzas; convenio-scope tables are default
  // estimates of what fiado agents get on convenio fianzas.
  const { propias: tarifasPropias, convenio: tarifasConvenio } = useMemo(
    () => partitionTarifas(tarifas),
    [tarifas]
  );

  // Derived data from contracts
  const afianzadoras = useMemo(() => {
    const set = new Set();
    contractsList.forEach((contract) => {
      (contract.fianzas || []).forEach((fianza) => {
        const name =
          fianza.xmlFactura?.emisor?.nombre ||
          fianza.xmlFianza?.emisor?.nombre ||
          "";
        if (name) set.add(name);
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "es"));
  }, [contractsList]);

  // Emisor name -> RFC, from the org's own fianzas. Stamped on new tarifas so
  // matching survives the name variants between fianza XML and CFDI
  // ("ASEGURADORA ASERTA" vs "..., S.A. DE C.V.").
  const rfcByEmisor = useMemo(() => {
    const map = new Map();
    contractsList.forEach((contract) => {
      (contract.fianzas || []).forEach((fianza) => {
        const name =
          fianza.xmlFactura?.emisor?.nombre ||
          fianza.xmlFianza?.emisor?.nombre ||
          "";
        const rfc =
          fianza.xmlFactura?.emisor?.rfc ||
          fianza.xmlFianza?.emisor?.rfc ||
          null;
        if (name && rfc && !map.has(name)) map.set(name, rfc);
      });
    });
    return map;
  }, [contractsList]);

  const subRamoTipoByAfianzadora = useMemo(() => {
    const map = new Map();
    contractsList.forEach((contract) => {
      (contract.fianzas || []).forEach((fianza) => {
        const emisorName =
          fianza.xmlFactura?.emisor?.nombre ||
          fianza.xmlFianza?.emisor?.nombre ||
          "";
        const subRamo = fianza.xmlFianza?.subRamo || fianza.subRamo || "";
        const tipo = fianza.tipoFianza || "";

        if (emisorName) {
          if (!map.has(emisorName)) {
            map.set(emisorName, new Set());
          }
          if (subRamo && tipo) {
            map.get(emisorName).add(`${subRamo} - ${tipo}`);
          } else if (subRamo) {
            map.get(emisorName).add(subRamo);
          } else if (tipo) {
            map.get(emisorName).add(tipo);
          }
        }
      });
    });

    const result = new Map();
    map.forEach((combinationsSet, emisor) => {
      result.set(emisor, Array.from(combinationsSet).sort((a, b) => a.localeCompare(b, "es")));
    });
    return result;
  }, [contractsList]);

  const availableSubRamoTipo = useMemo(() => {
    if (!newTarifa.emisor) return [];
    return subRamoTipoByAfianzadora.get(newTarifa.emisor) || [];
  }, [newTarifa.emisor, subRamoTipoByAfianzadora]);

  const toggleTarifaExpanded = (tarifaId) => {
    setExpandedTarifas(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tarifaId)) {
        newSet.delete(tarifaId);
      } else {
        newSet.add(tarifaId);
      }
      return newSet;
    });
  };

  const isTarifaExpanded = (tarifaId) => expandedTarifas.has(tarifaId);

  const validateTramos = (tramos) => {
    for (const tramo of tramos) {
      if (tramo.min === undefined || tramo.max === undefined || Number.isNaN(tramo.min) || Number.isNaN(tramo.max)) {
        setTarifasError("Completa todos los rangos de tarifa.");
        return false;
      }
      if (tramo.min < tramo.max) {
        setTarifasError("El primer valor de tarifa debe ser mayor o igual que el segundo en cada rango.");
        return false;
      }
    }
    return true;
  };

  const addTarifa = async () => {
    if (!user) return;
    if (!newTarifa.emisor.trim()) {
      setTarifasError("El emisor es obligatorio.");
      return;
    }
    if (!validateTramos(newTarifa.tramos)) {
      return;
    }
    setTarifasError(null);
    setSavingTarifaId("new");
    try {
      const emisorNombre = newTarifa.emisor.trim();
      const newTarifaData = {
        emisor: emisorNombre,
        subRamo: newTarifa.subRamo.trim(),
        tipo: newTarifa.tipo.trim(),
        tramos: newTarifa.tramos,
        createdBy: user.uid,
        createdAt: new Date(),
        ...(rfcByEmisor.get(emisorNombre) && { emisorRfc: rfcByEmisor.get(emisorNombre) }),
        ...(userData?.org && { org: userData.org }),
      };

      const docRef = await addDoc(collection(db, "tarifas"), newTarifaData);

      await updateDoc(doc(db, "usuarios", user.uid), {
        tarifas: arrayUnion(docRef.id),
      });

      // Add to local state
      const creada = { id: docRef.id, ...newTarifaData };
      setTarifas(prev => [...prev, creada]);
      // A new table may out-match whatever priced existing pending comisiones.
      proposeRetarifa(creada, [...tarifas, creada], true);

      setExpandedTarifas(prev => {
        const newSet = new Set(prev);
        newSet.add(docRef.id);
        return newSet;
      });

      setNewTarifa({
        emisor: "",
        subRamo: "",
        tipo: "",
        tramos: defaultTramos.map((t) => ({ ...t })),
      });
      setIsCreatingNewTarifa(false);
    } catch (error) {
      console.error("Error creando tarifa:", error);
      setTarifasError("No se pudo crear la tarifa.");
    } finally {
      setSavingTarifaId(null);
    }
  };

  // The default table of each ámbito: prices any afianzadora without its own
  // tabla, always flagged "por confirmar" (pctEstimado). One per ámbito.
  // The convenio one replaces the implicit fallback to the org's own tables —
  // its own % is a poor proxy for what the fiado's agent earns.
  const tarifaGeneralPropia = useMemo(
    () => tarifasPropias.find((t) => t.esGeneral) || null,
    [tarifasPropias]
  );
  const tarifaGeneralConvenio = useMemo(
    () => tarifasConvenio.find((t) => t.esGeneral) || null,
    [tarifasConvenio]
  );
  const tarifaGeneral =
    seccion === "convenios" ? tarifaGeneralConvenio : tarifaGeneralPropia;

  const addTarifaGeneral = async () => {
    if (!user || tarifaGeneral) return;
    const esConvenio = seccion === "convenios";
    setTarifasError(null);
    setSavingTarifaId("general");
    try {
      const data = {
        esGeneral: true,
        emisor: "",
        subRamo: "",
        tipo: "",
        tramos: defaultTramos.map((t) => ({ ...t })),
        createdBy: user.uid,
        createdAt: new Date(),
        ...(esConvenio && { ambito: TARIFA_AMBITO_CONVENIO }),
        ...(userData?.org && { org: userData.org }),
      };
      const docRef = await addDoc(collection(db, "tarifas"), data);
      await updateDoc(doc(db, "usuarios", user.uid), {
        tarifas: arrayUnion(docRef.id),
      });
      const creada = { id: docRef.id, ...data };
      setTarifas((prev) => [...prev, creada]);
      // The general table can now price every movement nothing priced before.
      proposeRetarifa(creada, [...tarifas, creada], true);
      setExpandedTarifas((prev) => new Set(prev).add(docRef.id));
    } catch (error) {
      console.error("Error creando tabla general:", error);
      setTarifasError("No se pudo crear la Tabla general.");
    } finally {
      setSavingTarifaId(null);
    }
  };

  const updateTarifa = async (tarifaId, tramos) => {
    if (!validateTramos(tramos)) return;
    setSavingTarifaId(tarifaId);
    try {
      await updateDoc(doc(db, "tarifas", tarifaId), { tramos });
      setTarifas((prev) =>
        prev.map((t) => (t.id === tarifaId ? { ...t, tramos } : t))
      );
      const original = tarifas.find((t) => t.id === tarifaId);
      if (original) {
        const editada = { ...original, tramos };
        proposeRetarifa(
          editada,
          tarifas.map((t) => (t.id === tarifaId ? editada : t)),
          false
        );
      }
    } catch (error) {
      console.error("Error actualizando tarifa:", error);
      setTarifasError("No se pudo actualizar la tarifa.");
    } finally {
      setSavingTarifaId(null);
    }
  };

  const deleteTarifa = async (tarifaId) => {
    if (!user) return;

    const confirmDelete = window.confirm(
      "¿Estás seguro de que deseas eliminar esta tabla de comisiones? Esta acción no se puede deshacer."
    );

    if (!confirmDelete) return;

    setSavingTarifaId(tarifaId);
    setTarifasError(null);

    try {
      await updateDoc(doc(db, "usuarios", user.uid), {
        tarifas: arrayRemove(tarifaId),
      });

      await deleteDoc(doc(db, "tarifas", tarifaId));

      setTarifas((prev) => prev.filter((t) => t.id !== tarifaId));

      setExpandedTarifas(prev => {
        const newSet = new Set(prev);
        newSet.delete(tarifaId);
        return newSet;
      });
    } catch (error) {
      console.error("Error eliminando tarifa:", error);
      setTarifasError("No se pudo eliminar la tarifa.");
    } finally {
      setSavingTarifaId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Heading level={2}>Comisiones</Heading>
        <div className="inline-flex rounded-lg border border-azul-surelia/20 overflow-hidden">
          <button
            type="button"
            onClick={() => { setSeccion("propias"); setIsCreatingNewTarifa(false); }}
            className={`px-3 py-1.5 text-sm ${seccion === "propias" ? "bg-azul-surelia text-white" : "bg-white text-azul-surelia"}`}
          >
            Mis tablas
          </button>
          <button
            type="button"
            onClick={() => { setSeccion("convenios"); setIsCreatingNewTarifa(false); }}
            className={`px-3 py-1.5 text-sm ${seccion === "convenios" ? "bg-azul-surelia text-white" : "bg-white text-azul-surelia"}`}
          >
            Convenios
          </button>
        </div>
      </div>

      {seccion === "convenios" && <ParticipacionConvenios afianzadoras={afianzadoras} />}

      {/* Convenios only take the Tabla general: the fiado agent's % is not
          ours to know per afianzadora, and it is confirmed on the emitted
          fianza anyway. Existing per-afianzadora convenio tables still price. */}
      {seccion === "propias" && renderCreateCard()}

      {seccion === "propias" ? (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <Heading level={3} className="text-xl">Mis tablas de comisiones</Heading>
            {!tarifasLoading && !tarifaGeneralPropia && (
              <Button outline onClick={addTarifaGeneral} disabled={savingTarifaId === "general"}>
                {savingTarifaId === "general" ? "Creando..." : "Crear Tabla general"}
              </Button>
            )}
          </div>
          {!tarifasLoading && !tarifaGeneralPropia && (
            <Text className="text-sm text-azul-surelia/70">
              La Tabla general estima la comisión de las afianzadoras sin tabla
              propia; esas comisiones quedan marcadas por confirmar.
            </Text>
          )}
          {tarifasLoading ? (
            <Text>Cargando tarifas...</Text>
          ) : tarifasPropias.length === 0 ? (
            <Text className="text-sm text-azul-surelia/70">No tienes tablas guardadas.</Text>
          ) : (
            [...tarifasPropias]
              .sort((a, b) => (b.esGeneral ? 1 : 0) - (a.esGeneral ? 1 : 0))
              .map(renderTarifaCard)
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <Heading level={3} className="text-xl">Tarifarios de convenios</Heading>
            {!tarifasLoading && !tarifaGeneralConvenio && (
              <Button outline onClick={addTarifaGeneral} disabled={savingTarifaId === "general"}>
                {savingTarifaId === "general" ? "Creando..." : "Crear Tabla general"}
              </Button>
            )}
          </div>
          {tarifasLoading ? (
            <Text>Cargando tarifas...</Text>
          ) : tarifasConvenio.length === 0 ? (
            <Text className="text-sm text-azul-surelia/70">No tienes tablas de convenios.</Text>
          ) : (
            [...tarifasConvenio]
              .sort((a, b) => (b.esGeneral ? 1 : 0) - (a.esGeneral ? 1 : 0))
              .map(renderTarifaCard)
          )}
        </div>
      )}

      <RetarifaComisionesDialog
        isOpen={!!retarifa}
        onClose={() => setRetarifa(null)}
        plan={retarifa?.plan || []}
        tarifaId={retarifa?.tarifaId}
      />
    </div>
  );

  function renderCreateCard() {
    return (
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="flex justify-between items-center">
          <Heading level={3} className="text-xl">Nueva tabla de comisiones</Heading>
          {!isCreatingNewTarifa && (
            <Button onClick={() => setIsCreatingNewTarifa(true)}>
              Nueva tabla
            </Button>
          )}
        </div>

        {isCreatingNewTarifa && (
          <>
            {tarifasError && <Text className="text-red-600 text-sm">{tarifasError}</Text>}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field>
                <Label className="text-azul-surelia font-semibold">Afianzadora / Emisor</Label>
                <Listbox
                  value={newTarifa.emisor}
                  onChange={(value) => {
                    setNewTarifa((prev) => ({
                      ...prev,
                      emisor: value,
                      subRamo: "",
                      tipo: ""
                    }));
                  }}
                  className="border-azul-surelia/30"
                >
                  <ListboxOption value="">
                    <ListboxLabel>Selecciona una afianzadora</ListboxLabel>
                  </ListboxOption>
                  {afianzadoras.map((name) => (
                    <ListboxOption key={name} value={name}>
                      <ListboxLabel>{name}</ListboxLabel>
                    </ListboxOption>
                  ))}
                </Listbox>
              </Field>
              <Field>
                <Label className="text-azul-surelia font-semibold">SubRamo - Tipo (opcional)</Label>
                <Listbox
                  value={newTarifa.subRamo && newTarifa.tipo ? `${newTarifa.subRamo} - ${newTarifa.tipo}` : newTarifa.subRamo || newTarifa.tipo || ""}
                  onChange={(value) => {
                    if (!value) {
                      setNewTarifa((prev) => ({ ...prev, subRamo: "", tipo: "" }));
                    } else if (value.includes(" - ")) {
                      const [subRamo, tipo] = value.split(" - ");
                      setNewTarifa((prev) => ({ ...prev, subRamo: subRamo.trim(), tipo: tipo.trim() }));
                    } else {
                      setNewTarifa((prev) => ({ ...prev, subRamo: value, tipo: "" }));
                    }
                  }}
                  disabled={!newTarifa.emisor || availableSubRamoTipo.length === 0}
                  className="border-azul-surelia/30"
                >
                  <ListboxOption value="">
                    <ListboxLabel>
                      {!newTarifa.emisor
                        ? "Primero selecciona una afianzadora"
                        : availableSubRamoTipo.length === 0
                        ? "No hay combinaciones disponibles"
                        : "Sin subramo-tipo específico"}
                    </ListboxLabel>
                  </ListboxOption>
                  {availableSubRamoTipo.map((combination) => (
                    <ListboxOption key={combination} value={combination}>
                      <ListboxLabel>{combination}</ListboxLabel>
                    </ListboxOption>
                  ))}
                </Listbox>
              </Field>
            </div>
            <div className="overflow-x-auto border rounded-lg">
              <Table>
                <TableHead className="bg-azul-surelia text-white">
                  <TableRow>
                    <TableHeader className="text-white">Límite Superior</TableHeader>
                    <TableHeader className="text-white">Límite Inferior</TableHeader>
                    <TableHeader className="text-white">% Comisión sobre prima</TableHeader>
                    <TableHeader className="text-white w-16"></TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {newTarifa.tramos.map((tramo, idx) => (
                    <TableRow key={`new-tramo-${idx}`}>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={tramo.min}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            setNewTarifa((prev) => {
                              const tramos = [...prev.tramos];
                              tramos[idx] = { ...tramos[idx], min: val };
                              return { ...prev, tramos };
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={tramo.max}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            setNewTarifa((prev) => {
                              const tramos = [...prev.tramos];
                              tramos[idx] = { ...tramos[idx], max: val };
                              return { ...prev, tramos };
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={tramo.comision}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value);
                            setNewTarifa((prev) => {
                              const tramos = [...prev.tramos];
                              tramos[idx] = { ...tramos[idx], comision: val };
                              return { ...prev, tramos };
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          color="white"
                          outline
                          onClick={() => {
                            setNewTarifa((prev) => ({
                              ...prev,
                              tramos: prev.tramos.filter((_, i) => i !== idx),
                            }));
                          }}
                          disabled={newTarifa.tramos.length <= 1}
                        >
                          Eliminar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex justify-between">
              <Button
                outline
                color="surelia"
                onClick={() =>
                  setNewTarifa((prev) => ({
                    ...prev,
                    tramos: [
                      ...prev.tramos,
                      { min: minTarifaDefault.min, max: minTarifaDefault.max, comision: minTarifaDefault.comision },
                    ],
                  }))
                }
              >
                Agregar rango
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsCreatingNewTarifa(false);
                    setTarifasError(null);
                    setNewTarifa({
                      emisor: "",
                      subRamo: "",
                      tipo: "",
                      tramos: defaultTramos.map((t) => ({ ...t })),
                    });
                  }}
                  disabled={savingTarifaId === "new"}
                >
                  Cancelar
                </Button>
                <Button onClick={addTarifa} disabled={savingTarifaId === "new"}>
                  {savingTarifaId === "new" ? "Guardando..." : "Guardar tabla"}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  function renderTarifaCard(tarifa) {
    return (
            <div key={tarifa.id} className="border rounded-lg overflow-hidden mb-4">
              {/* Header - Siempre visible */}
              <div
                className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 bg-beige-suave/10 cursor-pointer hover:bg-beige-suave/20 transition-colors"
                onClick={() => toggleTarifaExpanded(tarifa.id)}
              >
                <div className="flex items-center gap-3">
                  <svg
                    className={`w-5 h-5 text-azul-surelia transition-transform ${
                      isTarifaExpanded(tarifa.id) ? 'rotate-90' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <div>
                    <Heading level={4} className="text-lg">
                      {tarifa.esGeneral ? "Tabla general" : tarifa.emisor}
                    </Heading>
                    <Text className="text-sm text-azul-surelia/70">
                      {tarifa.esGeneral
                        ? tarifa.ambito === TARIFA_AMBITO_CONVENIO
                          ? "Por defecto — convenios sin tabla por afianzadora (por confirmar)"
                          : "Por defecto — afianzadoras sin tabla propia (por confirmar)"
                        : tarifa.subRamo && tarifa.tipo
                        ? `${tarifa.subRamo} - ${tarifa.tipo}`
                        : tarifa.subRamo
                        ? tarifa.subRamo
                        : tarifa.tipo
                        ? tarifa.tipo
                        : "Sin especificar"}
                    </Text>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-2 sm:mt-0" onClick={(e) => e.stopPropagation()}>
                  <Text className="text-xs text-gray-500 self-center">
                    {tarifa.tramos?.length || 0} rangos
                  </Text>
                  <Button
                    color="white"
                    outline
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteTarifa(tarifa.id);
                    }}
                    disabled={savingTarifaId === tarifa.id}
                    className="text-red-600 hover:text-red-700 hover:border-red-600"
                    title="Eliminar tabla de comisiones"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Content - Colapsable */}
              {isTarifaExpanded(tarifa.id) && (
                <div className="p-4 space-y-3 border-t border-azul-surelia/10">
                  <div className="flex justify-end gap-2">
                    <Button
                      outline
                      color="surelia"
                      onClick={(e) => {
                        e.stopPropagation();
                        setTarifas((prev) =>
                          prev.map((t) =>
                            t.id === tarifa.id
                              ? {
                                  ...t,
                                  tramos: [
                                    ...(t.tramos || []),
                                    { min: minTarifaDefault.min, max: minTarifaDefault.max, comision: minTarifaDefault.comision },
                                  ],
                                }
                              : t
                          )
                        );
                      }}
                    >
                      Agregar rango
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        updateTarifa(tarifa.id, tarifa.tramos);
                      }}
                      disabled={savingTarifaId === tarifa.id}
                      outline
                      color="surelia"
                    >
                      {savingTarifaId === tarifa.id ? "Guardando..." : "Guardar cambios"}
                    </Button>
                  </div>

                  <div className="overflow-x-auto border rounded-lg">
                    <Table>
                      <TableHead className="bg-azul-surelia text-white">
                        <TableRow>
                          <TableHeader className="text-white">Límite Superior</TableHeader>
                          <TableHeader className="text-white">Límite Inferior</TableHeader>
                          <TableHeader className="text-white">% Comisión</TableHeader>
                          <TableHeader className="text-white w-16"></TableHeader>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tarifa.tramos?.map((tramo, idx) => (
                          <TableRow key={`${tarifa.id}-tramo-${idx}`}>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={tramo.min}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value);
                                  setTarifas((prev) =>
                                    prev.map((t) => {
                                      if (t.id !== tarifa.id) return t;
                                      const tramos = [...(t.tramos || [])];
                                      tramos[idx] = { ...tramos[idx], min: val };
                                      return { ...t, tramos };
                                    })
                                  );
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={tramo.max}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value);
                                  setTarifas((prev) =>
                                    prev.map((t) => {
                                      if (t.id !== tarifa.id) return t;
                                      const tramos = [...(t.tramos || [])];
                                      tramos[idx] = { ...tramos[idx], max: val };
                                      return { ...t, tramos };
                                    })
                                  );
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                step="0.01"
                                value={tramo.comision}
                                onChange={(e) => {
                                  const val = parseFloat(e.target.value);
                                  setTarifas((prev) =>
                                    prev.map((t) => {
                                      if (t.id !== tarifa.id) return t;
                                      const tramos = [...(t.tramos || [])];
                                      tramos[idx] = { ...tramos[idx], comision: val };
                                      return { ...t, tramos };
                                    })
                                  );
                                }}
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                color="white"
                                outline
                                onClick={() => {
                                  setTarifas((prev) =>
                                    prev.map((t) => {
                                      if (t.id !== tarifa.id) return t;
                                      const tramos = t.tramos.filter((_, i) => i !== idx);
                                      return {
                                        ...t,
                                        tramos: tramos.length
                                          ? tramos
                                          : [{ min: minTarifaDefault.min, max: minTarifaDefault.max, comision: minTarifaDefault.comision }],
                                      };
                                    })
                                  );
                                }}
                                disabled={(tarifa.tramos || []).length <= 1}
                              >
                                Eliminar
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
    );
  }
};

export default ConfiguracionComisiones;
